const DAY_IN_MS = 24 * 60 * 60 * 1000;

const validDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatterFor = (timeZone, options) => {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, ...options });
  } catch (_error) {
    return new Intl.DateTimeFormat("en-US", options);
  }
};

const partValue = (parts, type) =>
  parts.find((part) => part.type === type)?.value || "";

const dateParts = (date, timeZone) => {
  const parts = formatterFor(timeZone, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);

  return {
    year: Number(partValue(parts, "year")),
    month: Number(partValue(parts, "month")),
    day: Number(partValue(parts, "day")),
  };
};

const calendarDayNumber = ({ year, month, day }) =>
  Date.UTC(year, month - 1, day) / DAY_IN_MS;

const formatTime = (date, timeZone) => {
  const parts = formatterFor(timeZone, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);
  const hour = partValue(parts, "hour");
  const minute = partValue(parts, "minute");
  const dayPeriod = partValue(parts, "dayPeriod").toLowerCase();

  return `${hour}:${minute}${dayPeriod}`;
};

export const normalizeMessageTimestamp = (value) =>
  validDate(value)?.toISOString() || null;

export const formatMessageTimestamp = (
  value,
  { now = new Date(), timeZone } = {},
) => {
  const messageDate = validDate(value);
  const currentDate = validDate(now);
  if (!messageDate || !currentDate) return null;

  const messageDay = dateParts(messageDate, timeZone);
  const currentDay = dateParts(currentDate, timeZone);
  const daysAgo = calendarDayNumber(currentDay) - calendarDayNumber(messageDay);
  const time = formatTime(messageDate, timeZone);

  if (daysAgo === 0) return time;
  if (daysAgo === 1) return `Yesterday ${time}`;
  if (daysAgo > 1 && daysAgo < 7) {
    const weekday = formatterFor(timeZone, { weekday: "long" }).format(messageDate);
    return `${weekday} ${time}`;
  }

  const dateLabel = formatterFor(timeZone, {
    month: "short",
    day: "numeric",
    ...(messageDay.year === currentDay.year ? {} : { year: "numeric" }),
  }).format(messageDate);

  return `${dateLabel} ${time}`;
};

export const formatMessageTimestampTitle = (value, { timeZone } = {}) => {
  const date = validDate(value);
  if (!date) return null;

  return formatterFor(timeZone, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  }).format(date);
};
